import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Content, useContent } from 'src/contexts/ContentContext';
import { Checkbox } from 'src/core/interim/Checkbox';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Button } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { ContentId } from 'src/utils/constants/contentId';
import { TAG_VALUE_TYPE_TO_CID } from 'src/utils/constants/contentIdMaps';
import { newIntId } from 'src/utils/idUtils';
import {
  Feature,
  Tag,
  TagsValueType,
  TagValueInheritMode,
  UserSetting,
} from 'src/WebApiController';

import { CancelButton } from '../../Buttons';
import * as styles from '../TagsFormBody.css';
import { CreateTagMetadataFormProps } from '../TagsFormBody.types';
import { useExistingTagNamesForPersistentTag } from '../useExistingTagNamesForPersistentTag';
import { TagValueInheritModeSelector } from './TagValueInheritModeSelector';

// Special characters that are not allowed in reporting column names to avoid interfering with the formula
const SPECIAL_CHAR_SET = ["'", '[', ']', '{', '}'];
const DEFAULT_MAX_NUM_REPORTING_COLUMN = 5;

// Reserved tag names that are not allowed to be created
const RESERVED_NAMES = ['SkyboxNotes'];

export const CreateTagMetadataForm = ({
  mergedTagsMetadata,
  disabled,
  onSubmit,
  onClose,
  entityType,
  useDialogStyle,
}: CreateTagMetadataFormProps) => {
  const {
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<Tag>({
    defaultValues: { key: '', value: '', valueType: TagsValueType.String },
  });

  const duplicateMsg = useContent(ContentId.ExistingTagName);
  const requiredMsg = useContent(ContentId.Required);
  const specialCharsMsg = useContent(ContentId.SpecialCharactersNotAllowed);
  const notAllowedMsg = useContent(ContentId.TagNameNotAllowed);

  const { value: maxNumReportingColumn = DEFAULT_MAX_NUM_REPORTING_COLUMN } =
    useServerUserSetting<number>({
      id: UserSetting.ReportingColumnLimit,
    });

  const hasPersistentTagCreateFeature = useUserHasFeature(
    Feature.PersistentTagCreate
  );

  const key = watch('key');
  const valueType = watch('valueType');
  const singleValueOnly = watch('singleValueOnly') ?? false;
  const allowMultipleValues = !singleValueOnly;
  const includeInReport = watch('includeInReport');
  const persistentTagId = watch('persistentTagId');
  const isPersistentTag = persistentTagId != null;
  const saleTagValueInheritModeId = watch('saleTagValueInheritModeId');

  const { existingTagNamesAcrossEntityTypes } =
    useExistingTagNamesForPersistentTag();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const validateTagInput = useCallback(
    (data: Tag) => {
      clearErrors('key');
      clearErrors('valueType');

      if (!data.key) {
        setError('key', { message: requiredMsg }, { shouldFocus: true });
        return false;
      }

      if (mergedTagsMetadata.find((t) => t.key === data.key) != null) {
        setError('key', { message: duplicateMsg }, { shouldFocus: true });
        return false;
      }

      if (
        data.persistentTagId &&
        existingTagNamesAcrossEntityTypes.includes(data.key)
      ) {
        setError('key', { message: duplicateMsg }, { shouldFocus: true });
        return false;
      }

      if (!data.valueType) {
        setError('valueType', { message: requiredMsg }, { shouldFocus: true });
        return false;
      }

      if (SPECIAL_CHAR_SET.some((c) => data.key.includes(c))) {
        setError('key', { message: specialCharsMsg }, { shouldFocus: true });
        return false;
      }

      if (RESERVED_NAMES.includes(data.key)) {
        setError('key', { message: notAllowedMsg }, { shouldFocus: true });
        return false;
      }

      if (isPersistentTag && data.saleTagValueInheritModeId == null) {
        setError(
          'saleTagValueInheritModeId',
          { message: requiredMsg },
          { shouldFocus: true }
        );
        return false;
      }

      return true;
    },
    [
      clearErrors,
      duplicateMsg,
      mergedTagsMetadata,
      notAllowedMsg,
      requiredMsg,
      setError,
      specialCharsMsg,
      existingTagNamesAcrossEntityTypes,
      isPersistentTag,
    ]
  );

  const onSubmitHandler = useCallback(
    async (newTag: Tag) => {
      // Validation
      if (!validateTagInput(newTag)) {
        return;
      }

      try {
        setIsSubmitting(true);
        await onSubmit(newTag);
        setIsSubmitting(false);
        onClose();
      } catch (e) {
        // error tracking is handled in the api hook, only doing console here
        console.error('Unable to save new tag metadata:', e);
      } finally {
        setIsSubmitting(false);
      }
    },
    [onClose, onSubmit, validateTagInput]
  );

  const tagTypesOptionsContent = useMemo(() => {
    return Object.entries(TAG_VALUE_TYPE_TO_CID).reduce(
      (cur, [key, value]) => {
        cur[key] = value;
        return cur;
      },
      {} as Record<string, string>
    );
  }, []);

  const willExceedReportingColumnLimit = useMemo(() => {
    const numReportingTags = mergedTagsMetadata.filter(
      (t) => t.includeInReport
    ).length;
    return numReportingTags >= maxNumReportingColumn;
  }, [maxNumReportingColumn, mergedTagsMetadata]);

  const renderCancelButton = useCallback(() => {
    return (
      <CancelButton
        onClick={onClose}
        disabled={disabled || isSubmitting}
        textContentId={ContentId.Cancel}
      />
    );
  }, [disabled, isSubmitting, onClose]);

  return (
    <div
      className={clsx(
        !useDialogStyle && styles.formMainContent,
        useDialogStyle && styles.formMainContentDialog
      )}
    >
      <PosFormField
        label={<Content id={ContentId.Name} />}
        errors={errors?.key?.message}
      >
        <PosTextField
          data-testid="create-tag-name-input"
          value={key}
          onChange={(e) => {
            clearErrors('key');
            setValue('key', e.target.value);
          }}
          disabled={disabled || isSubmitting}
          maxLength={750}
        />
      </PosFormField>

      <PosFormField
        label={<Content id={ContentId.ValueType} />}
        errors={errors?.valueType?.message}
      >
        <PosEnumSelect<TagsValueType>
          placeholderText={ContentId.SelectValueType}
          valueOptionsContent={tagTypesOptionsContent}
          value={valueType}
          onChange={(newValueType) => {
            clearErrors('valueType');
            setValue('valueType', newValueType);
          }}
          disabled={disabled || isSubmitting}
          style={{ width: '100%' }}
        />
      </PosFormField>
      <Checkbox
        labelPosition="right"
        checked={allowMultipleValues}
        onChange={() => {
          // isChecked is true means allow multiple values
          const isChecked = !allowMultipleValues;

          setValue('singleValueOnly', !isChecked);
          if (isChecked) {
            setValue('includeInReport', false);
          }
        }}
        disabled={disabled || isSubmitting || isPersistentTag}
        label={<Content id={ContentId.AllowMutipleValues} />}
      />

      <Checkbox
        labelPosition="right"
        disabled={
          disabled ||
          willExceedReportingColumnLimit ||
          !singleValueOnly ||
          isSubmitting
        }
        checked={includeInReport ?? false}
        onChange={() => {
          const isChecked = !includeInReport;
          setValue('includeInReport', isChecked);
        }}
        label={<Content id={ContentId.IncludeInColumns} />}
      />
      {hasPersistentTagCreateFeature && (
        <>
          <Checkbox
            labelPosition="right"
            disabled={disabled || isSubmitting}
            checked={isPersistentTag ?? false}
            onChange={() => {
              const isChecked = !isPersistentTag;
              clearErrors('key');
              if (isChecked) {
                setValue('persistentTagId', newIntId());
                setValue('singleValueOnly', false);
                setValue(
                  'saleTagValueInheritModeId',
                  TagValueInheritMode.SyncLiveValue
                );
              } else {
                setValue('persistentTagId', null);
                setValue('saleTagValueInheritModeId', null);
              }
            }}
            label={<Content id={ContentId.AddToPurchasesListingsAndSales} />}
          />

          {isPersistentTag && (
            <PosFormField
              label={<Content id={ContentId.SalesShould} />}
              errors={errors?.saleTagValueInheritModeId?.message}
            >
              <TagValueInheritModeSelector
                value={saleTagValueInheritModeId}
                onChange={(newsaleTagValueInheritModeId) => {
                  setValue(
                    'saleTagValueInheritModeId',
                    newsaleTagValueInheritModeId
                  );
                }}
              />
            </PosFormField>
          )}
        </>
      )}

      <div className={styles.formFooter}>
        {useDialogStyle && <>{renderCancelButton()}</>}
        <Button
          data-testid="create-tag-submit"
          onClick={() => {
            handleSubmit(onSubmitHandler)();
          }}
          disabled={disabled || isSubmitting}
        >
          <Content id={ContentId.Create} />
        </Button>

        {!useDialogStyle && <>{renderCancelButton()}</>}
      </div>
    </div>
  );
};
